import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ImageUpload } from "@/components/ImageUpload";

export default function MyInstitution() {
  const { refresh } = useAuth();
  const [filters, setFilters] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newCity, setNewCity] = useState("");

  useEffect(() => {
    api.get("/public/filters").then((r) => setFilters(r.data)).catch(() => {});
    api.get("/institution/profile").then((r) => setForm(r.data)).catch(() => {});
  }, []);

  if (!form) return <div className="h-64 animate-pulse rounded-xl bg-slate-200" />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const cities = filters?.cities?.filter((c) => c.country_id === form.country_id) || [];

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = (({ name, type, description, country_id, city_id, address, website, contact_person, phone, accreditation, logo }) =>
        ({ name, type, description, country_id, city_id, address, website, contact_person, phone, accreditation, logo }))(form);
      await api.put("/institution/profile", payload);
      await refresh();
      toast.success("Profile updated");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const requestCity = async () => {
    if (!newCity || !form.country_id) { toast.error("Select a country and enter a city name"); return; }
    try {
      const { data } = await api.post("/institution/request-city", { country_id: form.country_id, name: newCity });
      toast.success(data.message || "City submitted for approval");
      setNewCity("");
      const r = await api.get("/public/filters"); setFilters(r.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <h1 className="mb-1 font-heading text-2xl font-bold tracking-tight text-slate-900">My Institution</h1>
      <p className="mb-6 text-sm text-slate-500">Keep your institution profile up to date</p>
      <form onSubmit={save} className="space-y-4 rounded-xl border border-slate-200 bg-white p-6">
        <div className="grid gap-4 sm:grid-cols-2">
          <F label="Institution Name"><Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="inst-name" /></F>
          <F label="Type">
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{["University", "College", "Institute", "Business School"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Country">
            <Select value={form.country_id} onValueChange={(v) => { set("country_id", v); set("city_id", ""); }}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="max-h-60">{(filters?.countries || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="City">
            <Select value={form.city_id} onValueChange={(v) => set("city_id", v)} disabled={!form.country_id}>
              <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="max-h-60">{cities.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          <F label="Website"><Input value={form.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://" data-testid="inst-website" /></F>
          <F label="Logo"><ImageUpload value={form.logo} onChange={(v) => set("logo", v)} label="Logo" rounded /></F>
          <F label="Contact Person"><Input value={form.contact_person || ""} onChange={(e) => set("contact_person", e.target.value)} /></F>
          <F label="Phone"><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></F>
        </div>
        <F label="Address"><Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></F>
        <F label="Accreditation / Recognition"><Input value={form.accreditation || ""} onChange={(e) => set("accreditation", e.target.value)} /></F>
        <F label="Description"><Textarea rows={4} value={form.description || ""} onChange={(e) => set("description", e.target.value)} data-testid="inst-description" /></F>
        <Button type="submit" className="bg-blue-800 hover:bg-blue-900" disabled={saving} data-testid="save-inst-btn">
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </form>

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6">
        <h3 className="font-heading font-semibold text-slate-900">Can't find your city?</h3>
        <p className="mt-1 text-sm text-slate-500">Request a new city — it becomes available after admin approval.</p>
        <div className="mt-3 flex gap-2">
          <Input value={newCity} onChange={(e) => setNewCity(e.target.value)} placeholder="City name" data-testid="new-city-input" />
          <Button variant="outline" onClick={requestCity} data-testid="request-city-btn">Request City</Button>
        </div>
      </div>
    </div>
  );
}

function F({ label, children }) {
  return <div><Label className="mb-1.5 block">{label}</Label>{children}</div>;
}
